import type { ReactNode } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Tabs } from "@similarweb/ui-components";

import { ScorableTabs } from "app/components/React/ScorableTabs/ScorableTabs";
import {
    CompareTab,
    Icon,
    TabWrapperStyle,
} from "app/pages/website-analysis/TrafficAndEngagement/Components/StyledComponents";

import type { ConversionsOverTimeTabsProps } from "./types";

/**
 * Single mode keeps the score tabs with their headline values; compare mode uses the same
 * title-only tab row as the other over-time widgets on the page.
 */
export const ConversionsOverTimeTabs = ({
    tabs,
    selectedTab,
    isCompareMode,
    onSelect,
    children,
}: ConversionsOverTimeTabsProps): ReactNode => {
    const { translate } = useTranslate();

    if (!isCompareMode) {
        return (
            <ScorableTabs
                tabs={tabs}
                selectedTab={selectedTab}
                setSelected={onSelect}
            >
                {children}
            </ScorableTabs>
        );
    }

    const selectedIndex = tabs.findIndex((tab) => tab.metric === selectedTab.metric);

    return (
        <Tabs
            selectedIndex={selectedIndex >= 0 ? selectedIndex : 0}
            onSelect={(index: number) => onSelect(tabs[index])}
        >
            <TabWrapperStyle>
                {tabs.map((tab) => (
                    <CompareTab
                        key={tab.metric}
                        tooltipText={translate(tab.tooltip)}
                        data-automation={`conversions-over-time-tab-${tab.metric}`}
                    >
                        <Icon
                            size="xs"
                            iconName={tab.iconName}
                        />
                        {translate(tab.title)}
                    </CompareTab>
                ))}
            </TabWrapperStyle>
            {children}
        </Tabs>
    );
};
